import type React from "react";

import { useEditorState } from "@tiptap/react";
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  CircleQuestionMarkIcon,
  HeadingIcon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  Redo2Icon,
  StrikethroughIcon,
  UnderlineIcon,
  Undo2Icon,
} from "lucide-react";

import { styled } from "simplydo/react/styled";
import { mobileOnly, tabletUp } from "simplydo/react/theme";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import { useEditorContext } from "../editor/EditorContext";
import type { MenuCommands } from "../editor/commands";
import { Help } from "./Help";

export type { MenuCommands };

const StickyMenuBar = styled.div<{ $stickyOffset: number }>`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 4px;

  position: sticky;
  min-height: 40px;
  top: ${(props) => props.$stickyOffset}px;
  z-index: 11;
  background-color: white;
  padding: 5px 5px;
  box-shadow: 0 0 0 1px rgb(0, 0, 0, 0.1);
  margin-bottom: 10px;
  border-radius: 5px;

  ${mobileOnly} {
    overflow: auto;
    scrollbar-width: thin;
    padding-bottom: 4px;
  }
`;

const MenuBar = styled.div<{ $stickyOffset: number }>`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 4px;

  ${mobileOnly} {
    overflow: auto;
    scrollbar-width: thin;
    padding-bottom: 4px;
    margin-bottom: 0;
  }
`;

const Align = styled.div<{ $right?: boolean }>`
  display: flex;
  gap: 3px;
  flex-direction: row;
  justify-content: ${(props) => (props.$right ? "flex-end" : "flex-start")};

  ${mobileOnly} {
    flex-wrap: nowrap;
  }

  ${tabletUp} {
    flex-wrap: wrap;
  }
`;

const Separator = styled.div`
  width: 1px;
  background-color: #ddd;
  height: 18px;
  align-self: center;
  justify-self: center;
  margin-left: 4px;
  margin-right: 4px;
`;

const IconWrapper = styled.div<{ $isActive: boolean; $disabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  padding: 5px;

  background-color: none;
  box-shadow: ${({ $isActive }) =>
    $isActive ? "inset 4px 6px 11px 4px rgba(0,0,0,0.25)" : "none"};

  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  &:active {
    background-color: none;
    box-shadow: inset 2px 3px 8px 0px rgba(0, 0, 0, 0.15);
  }

  i,
  svg.icon {
    margin: 0;
  }
`;

const StyledIcon = styled(Icon)`
  && {
    flex-direction: row;
    font-size: 16px;
    position: relative;
  }
`;

const IconChar = styled.div`
  display: inline;
  left: -2px;
  vertical-align: top;
  top: -1px;
  position: relative;
`;

export type ExtraMenuItemAttrs = {
  name: string;
  commands: MenuCommands;
  selectedText: string;
  isActive: boolean;
};

export type ExtraMenuItem = {
  name: string;
  icon?: IconData;
  top?: number | undefined;
  left?: number | undefined;
  style?: React.CSSProperties;
  content?: React.ReactNode;
  forceEnable?: boolean;
  disabled?: boolean;
  onClick?: (attrs: ExtraMenuItemAttrs) => void;
  active?: () => boolean;
  MenuItem?: React.ComponentType<{
    RenderItem: React.ComponentType<ExtraMenuItemRenderProps>;
    itemProps: ExtraMenuItemRenderProps;
  }>;
};

type MenuIconProps = {
  isActive: boolean;
  name: IconData;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  children?: React.ReactNode;
  top?: number | undefined;
  left?: number | undefined;
  style?: React.CSSProperties | undefined;
  disabled?: boolean | undefined;
};

const MenuIcon = ({
  isActive,
  onClick,
  children,
  top,
  left,
  name,
  style,
  disabled,
  ...props
}: MenuIconProps) => (
  <IconWrapper
    onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
    }}
    onClick={onClick}
    $isActive={!!isActive}
    $disabled={!!disabled}
    style={style}
    {...props}
  >
    <StyledIcon
      icon={name}
      style={{
        ...(top != null ? { top } : null),
        ...(left != null ? { left } : null),
      }}
    />
    {children}
  </IconWrapper>
);

type ExtraMenuItemRenderProps = { item: ExtraMenuItem };

type EditMenuProps = {
  extraMenuItems?: Array<ExtraMenuItem> | undefined;
  sticky?: boolean;
  stickyOffset?: number;
  historyEnabled?: boolean;
};

export const EditMenu = ({
  extraMenuItems = [],
  sticky,
  stickyOffset,
  historyEnabled,
}: EditMenuProps) => {
  const { editor, commands: menuCommands } = useEditorContext();

  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      if (!instance || instance.isDestroyed) {
        return null;
      }
      const { from, to } = instance.state.selection;
      // Marks/nodes at the default selection (e.g. a leading link) must not
      // light up the menubar while the editor is blurred.
      const isFocused = instance.isFocused;
      return {
        bold: isFocused && instance.isActive("bold"),
        italic: isFocused && instance.isActive("italic"),
        underline: isFocused && instance.isActive("underline"),
        strike: isFocused && instance.isActive("strike"),
        heading1: isFocused && instance.isActive("heading", { level: 1 }),
        heading2: isFocused && instance.isActive("heading", { level: 2 }),
        bulletList: isFocused && instance.isActive("bulletList"),
        orderedList: isFocused && instance.isActive("orderedList"),
        alignmentCenter:
          isFocused && instance.isActive("alignment", { direction: "center" }),
        alignmentRight:
          isFocused && instance.isActive("alignment", { direction: "right" }),
        canUndo: instance.can().undo?.() ?? false,
        canRedo: instance.can().redo?.() ?? false,
        selectedText: instance.state.doc.textBetween(from, to, " "),
        extensionNames: instance.extensionManager.extensions.map((e) => e.name),
        activeNames: isFocused
          ? instance.extensionManager.extensions
              .filter((e) => instance.isActive(e.name))
              .map((e) => e.name)
          : [],
      };
    },
  });

  const getSelectedText = () => state?.selectedText ?? "";

  const renderExtraItem = (item: ExtraMenuItem, key: number) => {
    const hasBuiltIn =
      !!state?.extensionNames.includes(item.name) || item.forceEnable;
    if (!hasBuiltIn) return null;

    let isActive = false;
    try {
      isActive = item.active
        ? !!item.active()
        : !!state?.activeNames.includes(item.name);
    } catch {
      isActive = false;
    }

    const disabled = !!item.disabled;

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      item.onClick?.({
        name: item.name,
        commands: menuCommands,
        selectedText: getSelectedText(),
        isActive,
      });
    };

    const button = (
      <MenuIcon
        name={item.icon ?? CircleQuestionMarkIcon}
        top={item.top}
        left={item.left}
        style={item.style}
        disabled={disabled}
        isActive={isActive}
        onClick={handleClick}
      >
        {item.content}
      </MenuIcon>
    );

    if (item.MenuItem) {
      const Wrapper = item.MenuItem;
      const RenderItem = ({ item: _item }: ExtraMenuItemRenderProps) => button;
      return <Wrapper key={key} RenderItem={RenderItem} itemProps={{ item }} />;
    }

    return <span key={key}>{button}</span>;
  };

  const MenuComponent = sticky ? StickyMenuBar : MenuBar;

  return (
    <MenuComponent
      className="richtext-menubar"
      $stickyOffset={stickyOffset ?? 0}
    >
      <Align>
        <MenuIcon
          name={BoldIcon}
          isActive={!!state?.bold}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleBold?.();
          }}
        />
        <MenuIcon
          name={ItalicIcon}
          isActive={!!state?.italic}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleItalic?.();
          }}
        />
        <MenuIcon
          name={UnderlineIcon}
          top={2}
          isActive={!!state?.underline}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleUnderline?.();
          }}
        />
        <MenuIcon
          name={StrikethroughIcon}
          isActive={!!state?.strike}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleStrike?.();
          }}
        />
        <Separator />

        <MenuIcon
          name={HeadingIcon}
          isActive={!!state?.heading1}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleHeading?.({ level: 1 });
          }}
        >
          <IconChar>1</IconChar>
        </MenuIcon>
        <MenuIcon
          name={HeadingIcon}
          isActive={!!state?.heading2}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleHeading?.({ level: 2 });
          }}
        >
          <IconChar>2</IconChar>
        </MenuIcon>
        <Separator />

        <MenuIcon
          name={ListIcon}
          isActive={!!state?.bulletList}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleBulletList?.();
          }}
        />
        <MenuIcon
          name={ListOrderedIcon}
          isActive={!!state?.orderedList}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleOrderedList?.();
          }}
        />
        <Separator />

        <MenuIcon
          name={AlignLeftIcon}
          isActive={false}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.removeAlignment?.();
          }}
        />
        <MenuIcon
          name={AlignCenterIcon}
          isActive={!!state?.alignmentCenter}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.setAlignment?.({ direction: "center" });
          }}
        />
        <MenuIcon
          name={AlignRightIcon}
          isActive={!!state?.alignmentRight}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.setAlignment?.({ direction: "right" });
          }}
        />
        <Separator />

        {extraMenuItems.map((item, idx) => renderExtraItem(item, idx))}
      </Align>
      <Align $right>
        {historyEnabled ? (
          <>
            <MenuIcon
              name={Undo2Icon}
              disabled={!state?.canUndo}
              isActive={false}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                menuCommands.undo?.();
              }}
            />
            <MenuIcon
              name={Redo2Icon}
              disabled={!state?.canRedo}
              isActive={false}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                menuCommands.redo?.();
              }}
            />
            <Separator />
          </>
        ) : null}
        <Popup
          position="top right"
          wide="very"
          on="hover"
          trigger={
            <div>
              <MenuIcon isActive={false} name={CircleQuestionMarkIcon} />
            </div>
          }
        >
          {<Help />}
        </Popup>
      </Align>
    </MenuComponent>
  );
};
